import React, { useEffect } from 'react';

const ConsistentHashing = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Consistent Hashing in Distributed Systems - Complete Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master consistent hashing for distributed systems. Learn how to distribute keys evenly and handle node changes smoothly with minimal data movement.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master consistent hashing for distributed systems. Learn how to distribute keys evenly and handle node changes smoothly with minimal data movement.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Consistent Hashing in Distributed Systems</h1>
        <p style={styles.subtitle}>
          Distribute keys evenly across nodes and handle dynamic scaling with minimal data movement
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>What is Consistent Hashing?</h2>
          <p style={styles.paragraph}>
            Consistent hashing is a distributed hashing technique that minimizes the number of keys that need to be 
            remapped when the hash table is resized. Unlike traditional hashing where adding or removing a server requires 
            rehashing most keys, consistent hashing ensures that only a small fraction of keys need to be moved.
          </p>
          <p style={styles.paragraph}>
            The key benefit is that when a node is added or removed, only K/n keys need to be remapped on average, where 
            K is the total number of keys and n is the number of nodes. This makes it ideal for distributed systems that 
            need to scale dynamically.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>The Problem with Traditional Hashing</h2>
          <p style={styles.paragraph}>
            Traditional hashing uses a simple modulo operation to distribute keys across servers using the formula: 
            server_id = hash(key) % number_of_servers. This approach has a major problem when the number of servers changes.
          </p>
          <p style={styles.paragraph}>
            When you add or remove even one server, approximately 75% of keys need to be remapped to different servers. 
            For example, going from 3 servers to 4 servers causes most cached data to become invalid, leading to cache 
            storms and poor performance.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>How Consistent Hashing Works</h2>
          
          <h3 style={styles.heading}>The Hash Ring Concept</h3>
          <p style={styles.paragraph}>
            Consistent hashing maps both keys and servers to points on a circular hash ring (0 to 2^32 - 1). Each key 
            is assigned to the first server encountered when moving clockwise around the ring. This creates a consistent 
            mapping that minimizes changes when servers are added or removed.
          </p>
          <pre style={styles.codeBlock}>
{`Hash Ring Example:
                    Server A (hash: 100)
                         |
    Server C (300) ----  O  ---- Server B (200)
                         |
                    Key1 (150) → Server B
                    Key2 (250) → Server C
                    Key3 (350) → Server A`}
          </pre>

          <h3 style={styles.heading}>Implementation</h3>
          <p style={styles.paragraph}>
            Here's a basic implementation of consistent hashing that demonstrates the core concepts:
          </p>
          <pre style={styles.codeBlock}>
{`class ConsistentHash:
    def __init__(self):
        self.ring = {}  # hash_value -> server_name
        self.sorted_keys = []  # sorted hash values
    
    def add_server(self, server_name):
        hash_value = self.hash_function(server_name)
        self.ring[hash_value] = server_name
        self.sorted_keys.append(hash_value)
        self.sorted_keys.sort()
    
    def remove_server(self, server_name):
        hash_value = self.hash_function(server_name)
        del self.ring[hash_value]
        self.sorted_keys.remove(hash_value)
    
    def get_server(self, key):
        hash_value = self.hash_function(key)
        
        # Find the first server clockwise
        for server_hash in self.sorted_keys:
            if hash_value <= server_hash:
                return self.ring[server_hash]
        
        # Wrap around to the first server
        return self.ring[self.sorted_keys[0]]`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Virtual Nodes</h2>
          <p style={styles.paragraph}>
            To ensure better load distribution, each physical server is mapped to multiple points on the hash ring called 
            virtual nodes. This approach reduces the chance of hot spots and provides smoother scaling when servers are 
            added or removed.
          </p>
          <p style={styles.paragraph}>
            Virtual nodes provide several benefits: better load distribution across servers, smoother scaling when adding 
            or removing servers, and the ability to handle heterogeneous hardware by assigning more virtual nodes to more 
            powerful servers.
          </p>
          <pre style={styles.codeBlock}>
{`class ConsistentHashWithVNodes:
    def __init__(self, replicas=150):  # 150 virtual nodes per server
        self.replicas = replicas
        self.ring = {}
        self.sorted_keys = []
    
    def add_server(self, server_name):
        for i in range(self.replicas):
            # Create virtual node identifier
            vnode_key = f"{server_name}:{i}"
            hash_value = self.hash_function(vnode_key)
            self.ring[hash_value] = server_name
            self.sorted_keys.append(hash_value)
        self.sorted_keys.sort()
    
    def remove_server(self, server_name):
        for i in range(self.replicas):
            vnode_key = f"{server_name}:{i}"
            hash_value = self.hash_function(vnode_key)
            del self.ring[hash_value]
            self.sorted_keys.remove(hash_value)`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Production-Ready Implementation</h2>
          <p style={styles.paragraph}>
            Here's a more complete implementation that includes proper error handling, binary search for efficiency, 
            and support for replication:
          </p>
          <pre style={styles.codeBlock}>
{`import hashlib
import bisect
from typing import List, Dict, Optional

class ConsistentHashRing:
    def __init__(self, nodes: List[str] = None, replicas: int = 150):
        self.replicas = replicas
        self.ring: Dict[int, str] = {}
        self.sorted_keys: List[int] = []
        
        if nodes:
            for node in nodes:
                self.add_node(node)
    
    def _hash(self, key: str) -> int:
        """Generate hash value for a key"""
        return int(hashlib.md5(key.encode()).hexdigest(), 16)
    
    def add_node(self, node: str) -> None:
        """Add a node to the hash ring"""
        for i in range(self.replicas):
            virtual_key = f"{node}:{i}"
            hash_value = self._hash(virtual_key)
            self.ring[hash_value] = node
            bisect.insort(self.sorted_keys, hash_value)
    
    def remove_node(self, node: str) -> None:
        """Remove a node from the hash ring"""
        for i in range(self.replicas):
            virtual_key = f"{node}:{i}"
            hash_value = self._hash(virtual_key)
            if hash_value in self.ring:
                del self.ring[hash_value]
                self.sorted_keys.remove(hash_value)
    
    def get_node(self, key: str) -> Optional[str]:
        """Get the node responsible for a key"""
        if not self.ring:
            return None
        
        hash_value = self._hash(key)
        
        # Find the first node clockwise
        idx = bisect.bisect_right(self.sorted_keys, hash_value)
        if idx == len(self.sorted_keys):
            idx = 0  # Wrap around
        
        return self.ring[self.sorted_keys[idx]]
    
    def get_nodes(self, key: str, count: int) -> List[str]:
        """Get multiple nodes for replication"""
        if not self.ring or count <= 0:
            return []
        
        hash_value = self._hash(key)
        nodes = []
        seen_nodes = set()
        
        idx = bisect.bisect_right(self.sorted_keys, hash_value)
        
        while len(nodes) < count and len(seen_nodes) < len(set(self.ring.values())):
            if idx >= len(self.sorted_keys):
                idx = 0  # Wrap around
            
            node = self.ring[self.sorted_keys[idx]]
            if node not in seen_nodes:
                nodes.append(node)
                seen_nodes.add(node)
            
            idx += 1
        
        return nodes`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Use Cases</h2>
          
          <h3 style={styles.heading}>Distributed Caching</h3>
          <p style={styles.paragraph}>
            Cache keys are distributed across multiple cache servers using consistent hashing. When a cache server is 
            added or removed, only a fraction of keys need to be remapped, minimizing cache misses and maintaining 
            good performance during scaling operations.
          </p>

          <h3 style={styles.heading}>Content Delivery Networks</h3>
          <p style={styles.paragraph}>
            Content is distributed across edge servers based on consistent hashing of URLs or content IDs. This ensures 
            efficient content placement and retrieval while allowing CDNs to add or remove edge servers without massive 
            content redistribution.
          </p>

          <h3 style={styles.heading}>Database Sharding</h3>
          <p style={styles.paragraph}>
            Data is partitioned across database shards using consistent hashing, allowing for smooth scaling when adding 
            or removing database nodes. This is particularly useful for NoSQL databases that need to handle massive datasets.
          </p>

          <h3 style={styles.heading}>Load Balancing</h3>
          <p style={styles.paragraph}>
            Requests are distributed across backend servers using consistent hashing, ensuring that sessions or stateful 
            connections remain on the same server while allowing for graceful handling of server additions and removals.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Advantages and Limitations</h2>
          
          <h3 style={styles.heading}>Advantages</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Minimal Data Movement:</strong> Only K/n keys need to be remapped on average when nodes change
            </li>
            <li style={styles.listItem}>
              <strong>Scalability:</strong> Easy to add or remove nodes without major disruption to the system
            </li>
            <li style={styles.listItem}>
              <strong>Load Distribution:</strong> Virtual nodes ensure even load distribution across servers
            </li>
            <li style={styles.listItem}>
              <strong>Fault Tolerance:</strong> Node failures only affect adjacent keys, not the entire system
            </li>
          </ul>

          <h3 style={styles.heading}>Limitations</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Range Queries:</strong> Difficult to perform range queries across the hash ring since related 
              data may be scattered
            </li>
            <li style={styles.listItem}>
              <strong>Complexity:</strong> More complex to implement than simple hash-based distribution
            </li>
            <li style={styles.listItem}>
              <strong>Hot Spots:</strong> Can still create hot spots if keys are not uniformly distributed
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Choose the right number of virtual nodes:</strong> 100-200 virtual nodes per physical node 
              typically provides good load distribution without excessive overhead.
            </li>
            <li style={styles.listItem}>
              <strong>Use a good hash function:</strong> MD5 or SHA-1 provide good distribution properties for 
              consistent hashing applications.
            </li>
            <li style={styles.listItem}>
              <strong>Monitor load distribution:</strong> Track key distribution across nodes to identify imbalances 
              and hot spots that may require attention.
            </li>
            <li style={styles.listItem}>
              <strong>Plan for replication:</strong> Use consistent hashing to place replicas on different nodes 
              for fault tolerance and load distribution.
            </li>
            <li style={styles.listItem}>
              <strong>Handle node failures gracefully:</strong> Implement proper failover mechanisms and health 
              checks to detect and handle node failures.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            Consistent hashing is a fundamental technique for building scalable distributed systems. It solves the key 
            remapping problem of traditional hashing and enables smooth horizontal scaling with minimal data movement.
          </p>
          <p style={styles.paragraph}>
            While it adds complexity compared to simple hashing, the benefits of consistent hashing make it essential 
            for any distributed system that needs to scale dynamically. From caching layers to database shards to load 
            balancers, consistent hashing provides the foundation for elastic, scalable distributed systems.
          </p>
        </section>
      </main>
    </article>
  );
};

export default ConsistentHashing;